import React, { FC, useEffect, useState, useRef, useCallback } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useSelector } from 'react-redux';
import { Button as MuiButton, Tooltip, CircularProgress } from '@mui/material';
import LoadingButton from '@mui/material/Button';
import Cookies from 'js-cookie';
import { enqueueSnackbar } from 'notistack';
import AIModal from 'components/shared/AIModal/Index';
import { RootState } from 'redux/store';
import useLocales from 'hooks/useLocales';
import { useAIOperationCosts } from '../../../../hooks/useAIOperationCosts';
import { useAICreditAvailability } from 'hooks/useAICreditAvailability';
import { IJobForm, IJobIndustry } from 'core/interface/job';
import { IAIExtractedCompetencies } from 'core/interface/newJobPopup';
import { InputType } from 'core/enum/inputType';
import { degreeTypes, statusTypes } from 'core/constants/aboutMePopUpConstants';
import {
  freelanceSalary,
  monthlySalary,
  workExperience,
  hourlyRateChoices
} from 'core/constants/preferenceConstats';
import { throwIfAIResponseNotOk } from 'services/ai/ai';
import { JobService } from 'services/jobs/job';
import { ProfileService } from 'services/profile/profile';
import { SkillService } from 'services/skills/skillService';
import { JobPostRequiredSkill } from 'core/interface/escoSkill';
import {
  jobPostSkillsToApiPayload,
  normalizeJobPostSkillsFromApi
} from 'utils/jobPostSkills';
import {
  industriesInputToApiPayload
} from 'utils/jobIndustriesLocations';
import FormField from 'components/FormField/Index';
import Checkboxes from 'components/shared/Checkbox/Index';
import DropDown from 'components/Dropdown';
import Autocomplete from 'components/shared/Autocomplete/Index';
import Button from 'components/Button/Index';
import Image from 'components/image/Image';
import { initialValues, validation } from './form';
import Multiselect from 'components/shared/Multiselect/Index';
import { OptionType } from 'core/interface/autocomplete';
import WritableChip from 'components/shared/WritableChip/Index';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import SingleSelectFormik from 'components/shared/SelectSingleFormik';
import { jobContracts } from 'core/constants/workExperience';
import { useDebounce } from 'helpers/useDebounce';
import EscoSkillsPicker from 'components/shared/EscoSkillsPicker/Index';

import './Index.scss';
import { IMe } from 'core/interface/me';

const jobDescriptionQuillModules = {
  toolbar: [
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    [{ color: [] }, { background: [] }],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const jobDescriptionQuillFormats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'align',
  'link',
  'color',
  'background'
];

const NewJobPopup: FC<any> = ({ nextStep, selectedJob }): JSX.Element => {
  const { t } = useLocales();
  const { jobDescriptionCost, jobCompetenciesCost } = useAIOperationCosts();
  const jobDescriptionCredits = useAICreditAvailability(jobDescriptionCost);
  const jobCompetenciesCredits = useAICreditAvailability(jobCompetenciesCost);
  const { industry: industries } = useSelector(
    (state: RootState) => state.industries
  );
  const { cities } = useSelector((state: RootState) => state.locations);
  const [selectedIndustries, setSelectedIndustries] = useState<OptionType[]>(
    []
  );
  const [selectedLocations, setSelectedLocations] = useState<OptionType[]>([]);
  const [skills, setSkills] = useState<JobPostRequiredSkill[]>([]);
  const [savedSkills, setSavedSkills] = useState<JobPostRequiredSkill[]>([]);
  const [locationQuery, setLocationQuery] = useState<string>('');
  const debouncedLocationQuery = useDebounce(locationQuery, 500);
  const [errorsForFields, setErrors] = useState({
    industriesError: false,
    skillsError: false,
    locationsError: false
  });
  const { computerSkills } = useSelector(
    (state: RootState) => state.computerSkills
  );
  const me = useSelector((state: IMe) => state.user).user;

  const isAdmin = me?.role === 'admin';

  const getCities = async (q: string = '', limit: number = 10) => {
    JobService.getLocations({
      limit,
      q
    });
  };
  const { matchSearch } = useSelector((state: RootState) => state.match);
  const { assessmentByGroup } = useSelector(
    (state: RootState) => state.assessment
  );

  const getSearchProfile = (query: string = '') => {
    MatchProfileService.getMatchProfilesSearch(query);
  };

  useEffect(() => {
    getSearchProfile();
    getIndustries();
    getCities();
  }, []);

  useEffect(() => {
    const hasLegacyNumericSkills =
      Boolean(selectedJob?.id) &&
      Array.isArray(selectedJob?.skills) &&
      selectedJob.skills.some((skill: unknown) => typeof skill === 'number');

    if (hasLegacyNumericSkills && !computerSkills?.length) {
      SkillService.getITSkillsList('');
    }
  }, [selectedJob, computerSkills]);

  const getIndustries = (query: string = ''): void => {
    ProfileService.getIndustries(query);
  };

  const buildFormPayload = (values: IJobForm) => {
    const formattedLocations = selectedLocations.map((location) => ({
      id: location.id,
      display_name: location.title || location.name
    }));

    return {
      ...values,
      locations: formattedLocations,
      industries: getIndustriesPayload(),
      skills: jobPostSkillsToApiPayload(skills),
      edit: selectedJob?.id
    };
  };

  const proceedToNextStep = (
    values: IJobForm,
    resetForm: () => void,
    aiCompetencies?: IAIExtractedCompetencies
  ): void => {
    nextStep(buildFormPayload(values) as any, aiCompetencies);
    resetForm();
  };

  const handleSubmit = (
    values: IJobForm,
    { resetForm }: FormikHelpers<IJobForm>
  ): void => {
    proceedToNextStep(values, resetForm);
  };

  const getIndustriesPayload = (): IJobIndustry[] =>
    industriesInputToApiPayload(selectedIndustries, industries || []);

  const handleIndustryChange = (industry: OptionType[]): void => {
    setSelectedIndustries((prev) => {
      const newIndustries = industry.filter((newIndustry) => {
        return !prev.some(
          (existingIndustry) => existingIndustry.id === newIndustry.id
        );
      });
      return [...prev, ...newIndustries];
    });
    if (!selectedIndustries.length) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        industriesError: true
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        industriesError: false
      }));
    }
  };

  const handleLocationChange = (locations: OptionType[]): void => {
    setSelectedLocations((prev) => {
      const newLocations = locations.filter((newLocation) => {
        return !prev.some(
          (existingLocation) => existingLocation.id === newLocation.id
        );
      });
      return [...prev, ...newLocations];
    });
    if (!selectedLocations.length) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        locationsError: true
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        locationsError: false
      }));
    }
  };

  const handleRemoveIndustry = (e: any): void => {
    setSelectedIndustries((prev) =>
      prev.filter((ind) => (ind.title || ind.name || ind) !== e)
    );
  };

  const filterItems = (data: any, filterData: any) => {
    const selectedArray: any = [];

    if (!Array.isArray(data) || !Array.isArray(filterData)) {
      return selectedArray;
    }

    filterData.forEach((entry: any) => {
      if (typeof entry === 'object' && entry?.id != null) {
        const item = data.find((item: any) => item.id === entry.id);
        selectedArray.push(
          item || {
            id: entry.id,
            name: entry.name || entry.title || '',
            title: entry.name || entry.title || ''
          }
        );
        return;
      }

      const item = data.find((item: any) => item.id === entry);
      if (item) {
        selectedArray.push(item);
      }
    });

    return selectedArray;
  };

  // Function to fetch specific location IDs for a job
  const fetchLocationsByIds = async (locationIds: (string | number)[]) => {
    if (!locationIds || locationIds.length === 0) return [];

    // First try to find the locations in the already loaded cities
    const missingLocationIds = locationIds.filter(
      (id) => !cities?.some((city: any) => String(city.id) === String(id))
    );

    if (missingLocationIds.length === 0) return [];

    // Fetch each missing location individually by ID
    const fetchPromises = missingLocationIds.map((id) =>
      JobService.getLocationById(id)
    );

    try {
      // Wait for all location fetch operations to complete
      const results = await Promise.all(fetchPromises);
      return results.filter(Boolean);
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  };

  // Helper to extract location IDs from selectedJob
  const getLocationIdsFromJob = () => {
    if (!selectedJob?.locations) return [];

    return selectedJob.locations
      .map((loc: any) => {
        if (typeof loc === 'object' && loc !== null && loc.id) {
          return loc.id;
        }
        return loc;
      })
      .filter(Boolean);
  };

  // Helper to format a location object consistently
  const formatLocationObject = (loc: any) => {
    return {
      id: loc.id,
      title: loc.display_name,
      name: loc.display_name
    };
  };

  const formatLocationsFromSelectedJob = async () => {
    if (!selectedJob?.locations) return [];

    // Get all location IDs from the selected job
    const locationIds = getLocationIdsFromJob();

    // Fetch any missing locations
    const fetchedLocations = await fetchLocationsByIds(locationIds);

    const formattedLocations = selectedJob.locations
      .map((loc: any) => {
        // Handle if location is already an object with id and display_name
        if (
          typeof loc === 'object' &&
          loc !== null &&
          loc.id &&
          loc.display_name
        ) {
          return formatLocationObject(loc);
        }

        // Handle if location is just an ID (number or string)
        const locationId = typeof loc === 'object' ? loc.id : loc;
        const locationObj = cities?.find(
          (city: any) => String(city?.id) === String(locationId)
        );

        if (locationObj) {
          return formatLocationObject(locationObj);
        }

        // Check fetchedLocations as fallback
        const fetchedLocation = fetchedLocations?.find(
          (location: any) => String(location?.id) === String(locationId)
        );

        if (fetchedLocation) {
          return formatLocationObject(fetchedLocation);
        }

        return null;
      })
      .filter(Boolean);

    return formattedLocations;
  };

  useEffect(() => {
    if (
      selectedJob?.locations?.length > 0 &&
      (!cities || cities.length === 0)
    ) {
      getCities();
    }
  }, [selectedJob]);

  const initializedRef = useRef(false);
  const streamingAbortControllerRef = useRef<AbortController | null>(null);
  const competencyAbortControllerRef = useRef<AbortController | null>(null);
  const pendingFormValuesRef = useRef<IJobForm | null>(null);
  const pendingFormProceedRef = useRef<
    ((aiCompetencies?: IAIExtractedCompetencies) => void) | null
  >(null);
  const setFieldValueRef = useRef<
    ((field: string, value: unknown, shouldValidate?: boolean) => void) | null
  >(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isExtractingCompetencies, setIsExtractingCompetencies] =
    useState(false);
  const [openCompetencyAIModal, setOpenCompetencyAIModal] = useState(false);
  const isNewJob = !selectedJob?.id;

  useEffect(() => {
    initializedRef.current = false;
  }, [selectedJob?.id]);

  useEffect(
    () => () => {
      streamingAbortControllerRef.current?.abort();
      competencyAbortControllerRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    setFieldValueRef.current?.('industries', getIndustriesPayload(), true);
  }, [selectedIndustries]);

  useEffect(() => {
    setFieldValueRef.current?.(
      'locations',
      selectedLocations.map((location) => ({
        id: location.id,
        display_name: location.title || location.name || ''
      })),
      true
    );
  }, [selectedLocations]);

  useEffect(() => {
    setFieldValueRef.current?.(
      'skills',
      jobPostSkillsToApiPayload(skills),
      true
    );
  }, [skills]);

  const handleSkillsChange = (nextSkills: JobPostRequiredSkill[]): void => {
    setSkills(nextSkills);
    setErrors((prevErrors) => ({
      ...prevErrors,
      skillsError: nextSkills.length === 0
    }));
  };

  const mapCompetencyIds = (
    competencyIds: number[]
  ): IAIExtractedCompetencies => {
    const allCompetencies = assessmentByGroup.flatMap(
      (group: { competencies: { id: number; name: string }[] }) =>
        group.competencies
    );
    const labels: string[] = [];
    const ids: string[] = [];

    competencyIds.forEach((id) => {
      const competency = allCompetencies.find(
        (item: { id: number; name: string }) => item.id === id
      );
      if (competency) {
        labels.push(competency.name);
        ids.push(String(competency.id));
      }
    });

    return { ids, labels };
  };

  const handleAbortCompetencyExtraction = useCallback(() => {
    competencyAbortControllerRef.current?.abort();
    competencyAbortControllerRef.current = null;
    setOpenCompetencyAIModal(false);
    setIsExtractingCompetencies(false);
    pendingFormValuesRef.current = null;
    pendingFormProceedRef.current = null;
  }, []);

  const handleSkipCompetencyExtraction = useCallback(() => {
    const onProceed = pendingFormProceedRef.current;
    competencyAbortControllerRef.current?.abort();
    competencyAbortControllerRef.current = null;
    setOpenCompetencyAIModal(false);
    setIsExtractingCompetencies(false);
    pendingFormValuesRef.current = null;
    pendingFormProceedRef.current = null;
    onProceed?.();
  }, []);

  const extractCompetencies = async (
    values: IJobForm,
    helpers: Pick<FormikHelpers<IJobForm>, 'validateForm' | 'setTouched'>,
    onProceed: (aiCompetencies?: IAIExtractedCompetencies) => void
  ): Promise<void> => {
    const validationErrors = await helpers.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      helpers.setTouched(
        Object.keys(validationErrors).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        )
      );
      return;
    }

    pendingFormValuesRef.current = values;
    pendingFormProceedRef.current = onProceed;

    try {
      setIsExtractingCompetencies(true);
      setOpenCompetencyAIModal(true);

      competencyAbortControllerRef.current?.abort();
      competencyAbortControllerRef.current = new AbortController();

      const accessToken = Cookies.get('accesstoken');
      const response = await fetch('/api/ai/extract_competencies/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` })
        },
        body: JSON.stringify({
          job_title: values.job_title,
          job_description: values.job_description
        }),
        signal: competencyAbortControllerRef.current.signal
      });

      await throwIfAIResponseNotOk(response);

      const res = await response.json();
      const competencyIds: number[] =
        res.Competencies || res.competencies || [];

      if (!competencyIds.length) {
        enqueueSnackbar(
          t(
            'We could not suggest competencies right now. Please select them manually.'
          ),
          { variant: 'warning' }
        );
        onProceed();
        return;
      }

      const aiCompetencies = mapCompetencyIds(competencyIds);
      if (!aiCompetencies.labels.length) {
        enqueueSnackbar(
          t(
            'We could not suggest competencies right now. Please select them manually.'
          ),
          { variant: 'warning' }
        );
        onProceed();
        return;
      }

      onProceed(aiCompetencies);
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('Error extracting competencies:', error);
      enqueueSnackbar(error?.message || t('Failed to extract competencies'), {
        variant: 'error'
      });
    } finally {
      setIsExtractingCompetencies(false);
      setOpenCompetencyAIModal(false);
      competencyAbortControllerRef.current = null;
      pendingFormValuesRef.current = null;
      pendingFormProceedRef.current = null;
    }
  };

  const handleAbortJobDescription = useCallback(() => {
    if (streamingAbortControllerRef.current) {
      streamingAbortControllerRef.current.abort();
      streamingAbortControllerRef.current = null;
    }
    setIsGeneratingDescription(false);
  }, []);

  const updateStreamPreview = (text: string) => {
    setFieldValueRef.current?.('job_description', text, false);
  };

  const generateJobDescriptionStreaming = async (jobTitle: string) => {
    const setFieldValue = setFieldValueRef.current;
    if (!setFieldValue) {
      return;
    }

    if (!jobTitle?.trim()) {
      enqueueSnackbar(t('Job title is required to generate a description'), {
        variant: 'warning'
      });
      return;
    }

    let accumulatedDescription = '';

    try {
      setIsGeneratingDescription(true);
      setFieldValue('job_description', '', false);

      streamingAbortControllerRef.current?.abort();
      streamingAbortControllerRef.current = new AbortController();

      const accessToken = Cookies.get('accesstoken');
      const response = await fetch('/api/ai/generate_description/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` })
        },
        body: JSON.stringify({ job_title: jobTitle }),
        signal: streamingAbortControllerRef.current.signal
      });

      await throwIfAIResponseNotOk(response);

      if (!response.body) {
        const text = await response.text();
        accumulatedDescription = text;
        updateStreamPreview(text);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();

        if (done) {
          const finalChunk = decoder.decode();
          if (finalChunk) {
            accumulatedDescription += finalChunk;
            updateStreamPreview(accumulatedDescription);
          }
          streamDone = true;
          continue;
        }

        const decodedChunk = decoder.decode(value, { stream: true });
        if (!decodedChunk) {
          continue;
        }

        accumulatedDescription += decodedChunk;
        updateStreamPreview(accumulatedDescription);
      }

      reader.releaseLock();
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('Error generating job description:', error);
      enqueueSnackbar(
        error?.message || t('Failed to generate job description'),
        { variant: 'error' }
      );
    } finally {
      setIsGeneratingDescription(false);
      streamingAbortControllerRef.current = null;
    }
  };

  useEffect(() => {
    if (selectedJob && selectedJob.id && !initializedRef.current) {
      setSelectedIndustries(filterItems(industries, selectedJob?.industries));
      const normalizedSkills = normalizeJobPostSkillsFromApi(
        selectedJob,
        computerSkills
      );
      setSkills(normalizedSkills);
      setSavedSkills(normalizedSkills);

      // Handle the async formatLocationsFromSelectedJob function
      formatLocationsFromSelectedJob().then((locations) => {
        setSelectedLocations(locations);
        initializedRef.current = true;
      });
    }
  }, [selectedJob, cities, industries, computerSkills]);

  const normalizeDropdownValue = (
    val: unknown
  ): string | number | null => {
    if (val == null || val === '' || val === 0 || val === '0') return null;
    if (typeof val === 'string' || typeof val === 'number') return val;
    return Number(val);
  };

  const initialValuesForEdit: any = {
    skills,
    degree: normalizeDropdownValue(selectedJob?.degree),
    industries: selectedIndustries,
    is_public: selectedJob?.is_public ?? false,
    job_description: selectedJob?.description ?? '',
    job_title: selectedJob?.title ?? '',
    locations: selectedLocations,
    required_experience: normalizeDropdownValue(selectedJob?.work_exp),
    musts: selectedJob?.musts ?? [],
    culture_profile: selectedJob?.culture_profile ?? null,
    is_remote: selectedJob?.is_remote ?? false,
    if_office_work: selectedJob?.if_office_work ?? false,
    status: selectedJob?.status ?? '',
    contract_type: normalizeDropdownValue(
      selectedJob?.contract_type ?? selectedJob?.contract
    ),
    compensation: normalizeDropdownValue(selectedJob?.compensation)
  };

  const handleLoadMoreCities = () => {
    const params = { limit: 10, offset: cities.length };
    JobService.getLocations(params, true);
  };

  useEffect(() => {
    if (debouncedLocationQuery) {
      getCities(debouncedLocationQuery);
    } else {
      getCities();
    }
  }, [debouncedLocationQuery]);

  return (
    <div className="njob scrollable">
      <AIModal
        open={openCompetencyAIModal}
        onClose={handleAbortCompetencyExtraction}
        customMessage={t(
          'We are extracting competencies from the job information. This may take up to 1 minute. If you do not wish to wait, please click Skip to select competencies manually.'
        )}
        customButton={
          <MuiButton
            variant="outlined"
            onClick={handleSkipCompetencyExtraction}
            className="ai-modal-skip-btn"
          >
            {t('Skip')}
          </MuiButton>
        }
      />
      <Formik
        initialValues={selectedJob?.id ? initialValuesForEdit : initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange
        validateOnMount
        validationSchema={validation(t)}
      >
        {({
          setFieldValue,
          setFieldTouched,
          isValid,
          errors,
          touched,
          values,
          validateForm,
          setTouched,
          resetForm
        }) => {
          setFieldValueRef.current = setFieldValue;

          return (
            <Form>
              <div className="fields">
                <div className="field many job-title-field">
                  <span className="label">{t('Job title')}</span>
                  <div className="job-title-row">
                    <FormField
                      fieldName="job_title"
                      placeholder={t('Job title')}
                      type={InputType.text}
                      inputClassName="input"
                    />
                    <Tooltip
                      title={
                        jobDescriptionCredits.isCreditLimited
                          ? jobDescriptionCredits.outOfCreditsTooltip
                          : t(
                              `The operation will cost ${jobDescriptionCost} AI credits.`
                            )
                      }
                      placement="top"
                      arrow
                    >
                      <span style={{ display: 'inline-block' }}>
                        <Button
                          type="button"
                          t={t}
                          title={
                            isGeneratingDescription
                              ? t('Stop generation')
                              : t('Generate Job Description')
                          }
                          className="generate-description-btn"
                          onClick={() => {
                            if (isGeneratingDescription) {
                              handleAbortJobDescription();
                              return;
                            }
                            generateJobDescriptionStreaming(values.job_title);
                          }}
                          disabled={
                            !values.job_title?.trim() ||
                            jobDescriptionCredits.isCreditLimited
                          }
                          isLoading={isGeneratingDescription}
                          keepEnabledOnLoading
                        />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <div className="field many autocomplete">
                  <span className="label">{t('Select culture profile')}</span>
                  <SingleSelectFormik
                    fieldName="culture_profile"
                    onChange={(e: any) => {
                      if (typeof e != 'string') {
                        setFieldValue('culture_profile', e);
                        setFieldTouched('culture_profile', true, false);
                      }
                    }}
                    onBlur={(e: any) => {
                      const inputValue = e.target.value?.trim();
                      if (!inputValue) {
                        setFieldValue('culture_profile', null);
                        setFieldTouched('culture_profile', true);
                        return;
                      }
                      if (
                        matchSearch?.length &&
                        !matchSearch.some((el: any) => el.title === inputValue)
                      ) {
                        setFieldValue('culture_profile', null);
                        setFieldTouched('culture_profile', true);
                      }
                    }}
                    options={matchSearch}
                    show_location={false}
                    placeholder={''}
                    onInputChange={(e: any) => getSearchProfile(e)}
                    value={values?.culture_profile || ''}
                  />
                </div>
              </div>
              <div className="field esco-skills-field">
                <EscoSkillsPicker
                  value={skills}
                  onChange={handleSkillsChange}
                  jobTitle={values.job_title}
                  initialSavedSkills={savedSkills}
                  error={Boolean(
                    errors.skills &&
                      (errorsForFields.skillsError || touched.skills)
                  )}
                />
                <span className="error-msg">
                  {(errorsForFields.skillsError || touched.skills) &&
                    errors.skills}
                </span>
              </div>
              <div className="field job-description-field">
                <span className="label">
                  {t('Job description')}
                  {isGeneratingDescription && (
                    <span className="job-description-generating-label">
                      {t('Generating')}
                      <span
                        className="job-description-generating-dots"
                        aria-hidden="true"
                      >
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                      </span>
                    </span>
                  )}
                </span>
                <div
                  className={`job-description-editor-wrap ${
                    isGeneratingDescription
                      ? 'job-description-editor-wrap--generating'
                      : ''
                  }`}
                >
                  <ReactQuill
                    value={values.job_description || ''}
                    readOnly={isGeneratingDescription}
                    onChange={(content) => {
                      if (isGeneratingDescription) return;
                      setFieldValue('job_description', content);
                      setFieldTouched('job_description', true, false);
                    }}
                    modules={jobDescriptionQuillModules}
                    formats={jobDescriptionQuillFormats}
                    theme="snow"
                    className={`quill-editor job-description-editor ${
                      isGeneratingDescription
                        ? 'job-description-editor--generating'
                        : ''
                    } ${
                      errors.job_description && touched.job_description
                        ? 'error'
                        : ''
                    }`}
                    placeholder={t('Job description')}
                  />
                </div>
                {!isGeneratingDescription &&
                  errors.job_description &&
                  touched.job_description && (
                    <div className="error-container">
                      <span className="error-message-form-field">
                        {t(errors.job_description as string)}
                      </span>
                    </div>
                  )}
              </div>
              {isAdmin && (
                <div className="field">
                  <Checkboxes
                    onArrayChange={(e) => {
                      if (Array.isArray(e)) {
                        setFieldValue('is_public', e[0].isChecked);
                      }
                    }}
                    checkboxes={[
                      {
                        label: t('Show this job on my careerpage'),
                        isChecked: values?.is_public
                      }
                    ]}
                  />
                </div>
              )}
              <div className="fields">
                <div className="field many">
                  <span className="label">{t('Required Degree')}</span>
                  <DropDown
                    placeholder={t('Degree')}
                    items={degreeTypes}
                    selectedValue={(e) => setFieldValue('degree', e)}
                    defaultValue={normalizeDropdownValue(values.degree) ?? undefined}
                  />
                </div>
                <div className="field many">
                  <span className="label">{t('Locations')}</span>
                  <Multiselect
                    fieldName="locations"
                    value={selectedLocations}
                    className={`${
                      errors.locations && errorsForFields.locationsError
                        ? 'error'
                        : ''
                    }`}
                    placeholder={t('Country, province, city')}
                    show_location={true}
                    onChange={(e: string | OptionType[]) => {
                      if (typeof e != 'string' && Array.isArray(e)) {
                        handleLocationChange(e);
                      }
                    }}
                    onInputChange={(e: any) => {
                      // Handle both string and event object
                      const value =
                        typeof e === 'object' && e?.target ? e.target.value : e;
                      setLocationQuery(value || '');
                    }}
                    options={
                      cities?.map((e: any) => ({
                        id: e?.id,
                        title: e?.display_name,
                        name: e?.display_name
                      })) || []
                    }
                    loadMore={handleLoadMoreCities}
                  />
                  <div className="selected-skills">
                    {selectedLocations?.map((location, index) => (
                      <div key={location.id || index} className="skill">
                        <div className="skill-name">
                          {location.name ?? location?.title}
                        </div>
                        <div
                          className="remove"
                          onClick={() => {
                            setSelectedLocations((prev) =>
                              prev.filter((loc) => loc.id !== location.id)
                            );
                          }}
                        >
                          <Image name="close.svg" width={10} height={10} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <span className="error-msg">
                    {errorsForFields.locationsError &&
                      touched.locations &&
                      errors.locations &&
                      t('At least one location is required')}
                  </span>
                </div>
              </div>
              <div className="fields">
                <div className="field many">
                  <span className="label">{t('Industries')}</span>
                  <Multiselect
                    fieldName="industries"
                    value={
                      initialValuesForEdit?.industries ?? selectedIndustries
                    }
                    className={`${
                      errors.industries && errorsForFields.industriesError
                        ? 'error'
                        : ''
                    }`}
                    placeholder={t('Industry')}
                    show_location={false}
                    onChange={(e: string | OptionType[]) => {
                      if (typeof e != 'string') {
                        handleIndustryChange(e);
                      }
                    }}
                    onInputChange={(e) => getIndustries(e ?? '')}
                    options={
                      industries?.map((e) => ({
                        id: e?.id,
                        title: e?.name
                      })) || []
                    }
                  />
                  {selectedIndustries?.map((industry, index) => (
                    <span className="industry-selected" key={index}>
                      {industry.name ?? industry?.title}{' '}
                      <span
                        onClick={() => {
                          handleRemoveIndustry(
                            industry.name ?? industry?.title ?? industry ?? ''
                          );
                        }}
                      >
                        <Image
                          className="remove"
                          name="removes.svg"
                          width={10}
                          height={10}
                        />
                      </span>
                    </span>
                  ))}
                  <span className="error-msg">
                    {errorsForFields.industriesError &&
                      (typeof errors.industries === 'string'
                        ? errors.industries
                        : undefined)}
                  </span>
                </div>
                <div className="field many">
                  <span className="label">{t('Requirements')}</span>
                  <WritableChip
                    className={`${
                      errors.musts && touched.musts ? 'error' : ''
                    }`}
                    inputClassName="input"
                    placeholder={t('')}
                    onSelectedValues={(e: string[]) =>
                      setFieldValue('musts', Array.isArray(e) ? e : [], false)
                    }
                    defaultValues={values?.musts}
                    setTouch={() => setFieldTouched('musts', true, false)}
                  />
                  <span className="error-msg">
                    {touched.musts && errors.musts}
                  </span>
                </div>
              </div>
              <div className="fields work-mode-status-row">
                <div className="field many work-mode-checkboxes">
                  <Checkboxes
                    onArrayChange={(e) => {
                      if (Array.isArray(e)) {
                        setFieldValue('is_remote', e[0].isChecked);
                      }
                    }}
                    checkboxes={[
                      {
                        label: t('Remote work'),
                        isChecked: values?.is_remote
                      }
                    ]}
                  />
                  <Checkboxes
                    onArrayChange={(e) => {
                      if (Array.isArray(e)) {
                        setFieldValue('if_office_work', e[0].isChecked);
                      }
                    }}
                    checkboxes={[
                      {
                        label: t('Office work'),
                        isChecked: values?.if_office_work
                      }
                    ]}
                  />
                </div>
                <div className="field many">
                  <span className="label">{t('Status')}</span>
                  <DropDown
                    placeholder={t('Select status')}
                    items={statusTypes}
                    selectedValue={(e) => setFieldValue('status', e)}
                    defaultValue={values?.status}
                  />
                </div>
              </div>
              <div className="fields">
                {/* <div className="field many autocomplete">
                  <span className="label">{t('Prefered monthly salary')}</span>
                  <DropDown
                    dropdownClassName={`${
                      errors.prefered_salary && touched.prefered_salary
                        ? 'error'
                        : ''
                    }`}
                    items={monthlySalary}
                    selectedValue={(e) => setFieldValue('prefered_salary', e)}
                    defaultValue={initialValuesForEdit?.prefered_salary}
                  />
                </div> */}
              </div>
              <div className="fields">
                <div className="field many">
                  <span className="label">{t('Required work experience')}</span>
                  <DropDown
                    placeholder={t('Select experience')}
                    items={workExperience}
                    selectedValue={(e) =>
                      setFieldValue('required_experience', e)
                    }
                    defaultValue={
                      normalizeDropdownValue(values.required_experience) ??
                      undefined
                    }
                  />
                </div>
                <div className="field many">
                  <span className="label">{t('Contract')}</span>
                  <DropDown
                    placeholder={t('Select contract')}
                    items={jobContracts}
                    selectedValue={(e) => setFieldValue('contract_type', e)}
                    defaultValue={
                      normalizeDropdownValue(values.contract_type) ?? undefined
                    }
                  />
                </div>
                <div className="field many autocomplete">
                  <span className="label">
                    {values?.contract_type === 2
                      ? t('Hourly Rate (EUR)')
                      : t('Salary Range(EUR)')}
                  </span>
                  <DropDown
                    placeholder={
                      values?.contract_type === 2
                        ? t('Select hourly rate')
                        : t('Select salary range')
                    }
                    dropdownClassName={`${
                      errors.compensation && touched.compensation ? 'error' : ''
                    }`}
                    items={
                      values?.contract_type === 2
                        ? hourlyRateChoices
                        : monthlySalary
                    }
                    selectedValue={(e) => setFieldValue('compensation', e)}
                    defaultValue={
                      normalizeDropdownValue(values.compensation) ?? undefined
                    }
                  />
                </div>
              </div>
              {isNewJob ? (
                <div className="split-button">
                  <LoadingButton
                    type="submit"
                    className="split-button__half split-button__half--left"
                    disabled={!isValid}
                    style={{ textTransform: 'none' }}
                  >
                    {t('Select Competencies')}
                  </LoadingButton>
                  <Tooltip
                    title={
                      jobCompetenciesCredits.isCreditLimited
                        ? jobCompetenciesCredits.outOfCreditsTooltip
                        : t(
                            `The operation will cost ${jobCompetenciesCost} AI credits.`
                          )
                    }
                    placement="top"
                    arrow
                  >
                    <span className="split-button__tooltip-wrap">
                      <LoadingButton
                        type="button"
                        className="split-button__half split-button__half--right"
                        disabled={
                          !isValid ||
                          isExtractingCompetencies ||
                          jobCompetenciesCredits.isCreditLimited
                        }
                        style={{ textTransform: 'none' }}
                        onClick={() =>
                          extractCompetencies(
                            values,
                            { validateForm, setTouched },
                            (aiCompetencies) =>
                              proceedToNextStep(
                                values,
                                resetForm,
                                aiCompetencies
                              )
                          )
                        }
                      >
                        {isExtractingCompetencies ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          t('AI Competencies')
                        )}
                      </LoadingButton>
                    </span>
                  </Tooltip>
                </div>
              ) : (
                <Button
                  t={t}
                  type="submit"
                  title="Next"
                  className="save "
                  disabled={!isValid && !selectedJob?.id}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default NewJobPopup;
